import { useEffect, useState } from 'react';
import * as api from '../api';

export default function AssignmentsPage({ user }) {
  const [rows, setRows] = useState([]);
  const [videos, setVideos] = useState([]);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(false);
  const [formErr, setFormErr] = useState('');
  const [form, setForm] = useState({ title: '', videoId: '', dueDate: '', description: '' });
  const [confirm, setConfirm] = useState(null);
  const [selected, setSelected] = useState(null);
  const [submitText, setSubmitText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState({});
  const isAdmin = user?.role === 'admin';

  const load = async () => {
    try {
      const [aRes, vRes] = await Promise.all([api.getAssignments(), api.getVideos()]);
      setRows(aRes.data || []);
      setVideos(vRes.data || []);
    } catch (e) { setErr(e.response?.data?.error || 'Could not load'); }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setFormErr('');
    if (!form.title.trim()) { setFormErr('Title is required.'); return; }
    if (!form.videoId) { setFormErr('Please select a video.'); return; }
    try {
      await api.createAssignment({ video_id: form.videoId, title: form.title.trim(), description: form.description, due_date: form.dueDate || null });
      setForm({ title: '', videoId: '', dueDate: '', description: '' });
      setModal(false);
      load();
    } catch (e) { setFormErr(e.response?.data?.error || 'Create failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.deleteAssignment(id); setConfirm(null); load(); } catch (e) { console.error(e); }
  };

  const handleSubmit = async (assignmentId) => {
    if (!submitText.trim()) return;
    setSubmitting(true);
    try {
      await api.submitAssignment(assignmentId, { response_text: submitText.trim() });
      setSubmitted(prev => ({ ...prev, [assignmentId]: true }));
      setSubmitText('');
      load();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  if (err) return <div className="alert">{err}</div>;

  // Employee view — show assignment details with submit option
  if (!isAdmin && selected) {
    const vid = videos.find(v => v.id === selected.video_id);
    const isSubmitted = submitted[selected.id];
    return (
      <div>
        <button className="btn btn-secondary btn-sm" style={{ marginBottom: 16 }} onClick={() => setSelected(null)}>← Back to Assignments</button>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.title}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Linked: {selected.video_title || vid?.title || '-'} · Due: {selected.due_date || 'No deadline'}</div>
            </div>
            <span className={`badge ${isSubmitted ? 'badge-green' : 'badge-blue'}`}>{isSubmitted ? 'Submitted' : 'Available'}</span>
          </div>

          {selected.description && (
            <div style={{ fontSize: 13, padding: '12px 14px', background: 'var(--bg)', borderRadius: 6, marginBottom: 16, lineHeight: 1.6 }}>
              {selected.description}
            </div>
          )}

          {isSubmitted ? (
            <div className="notification">Your response has been submitted successfully.</div>
          ) : (
            <div>
              <div className="form-group">
                <label className="form-label">Your Response</label>
                <textarea
                  className="form-textarea"
                  style={{ minHeight: 150 }}
                  placeholder="Write your response here..."
                  value={submitText}
                  onChange={e => setSubmitText(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  disabled={!submitText.trim() || submitting}
                  onClick={() => handleSubmit(selected.id)}
                >
                  {submitting ? 'Submitting...' : 'Submit Assignment'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {confirm && (
        <div className="modal-overlay" onClick={() => setConfirm(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal">
              <div className="confirm-icon">🗑️</div>
              <div className="confirm-title">Delete Assignment</div>
              <div className="confirm-sub">Delete "{confirm.title}"? This cannot be undone.</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDelete(confirm.id)}>Yes, Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="section-header">
        <div className="section-title">Assignments</div>
        {isAdmin && <button className="btn btn-primary" onClick={() => { setFormErr(''); setForm({ title: '', videoId: videos[0]?.id || '', dueDate: '', description: '' }); setModal(true); }}>+ Create Assignment</button>}
      </div>

      {isAdmin ? (
        <div className="card">
          {rows.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--muted)', fontSize: 13 }}>No assignments yet.</div>
          ) : (
            <table>
              <thead><tr><th>Assignment</th><th>Linked Video</th><th>Due Date</th><th>Submissions</th><th>Completion</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {rows.map(a => {
                  const pct = a.total_employees > 0 ? Math.round((a.submissions / a.total_employees) * 100) : 0;
                  return (
                    <tr key={a.id}>
                      <td><div style={{ fontWeight: 500 }}>{a.title}</div>{a.description && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{a.description.slice(0, 55)}...</div>}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{a.video_title || '-'}</td>
                      <td style={{ fontSize: 12, color: 'var(--muted)' }}>{a.due_date || '-'}</td>
                      <td><span style={{ fontWeight: 600 }}>{a.submissions}</span><span style={{ color: 'var(--muted)' }}> / {a.total_employees}</span></td>
                      <td><div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><div className="progress-bar" style={{ width: 70 }}><div className="progress-fill" style={{ width: pct + '%', background: pct === 100 ? 'var(--success)' : pct < 30 ? 'var(--danger)' : 'var(--teal)' }}></div></div><span style={{ fontSize: 12 }}>{pct}%</span></div></td>
                      <td style={{ textAlign: 'right' }}><button className="btn-icon" onClick={() => setConfirm({ id: a.id, title: a.title })}>🗑️</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        // Employee view — card per assignment, click to open
        <div>
          {rows.length === 0 && <div className="card" style={{ textAlign: 'center', padding: 32, color: 'var(--muted)' }}>No assignments yet.</div>}
          {rows.map(a => (
            <div key={a.id} className="card" style={{ marginBottom: 16, cursor: 'pointer' }} onClick={() => { setSelected(a); setSubmitText(''); }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{a.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{a.video_title || '-'} · Due: {a.due_date || 'No deadline'}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`badge ${submitted[a.id] ? 'badge-green' : 'badge-blue'}`}>{submitted[a.id] ? 'Submitted' : 'Available'}</span>
                  <span className="btn btn-primary btn-sm">Open →</span>
                </div>
              </div>
              {a.description && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>{a.description.slice(0, 100)}...</div>}
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Create Assignment</span><button className="close-btn" onClick={() => setModal(false)}>×</button></div>
            {formErr && <div className="alert">{formErr}</div>}
            <div className="form-group"><label className="form-label">Title *</label><input className="form-input" placeholder="e.g. Safety Checklist Report" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Link to Video *</label>
              <select className="form-select" value={form.videoId} onChange={e => setForm(f => ({ ...f, videoId: e.target.value }))}>
                <option value="">— Select a video —</option>
                {videos.map(v => <option key={v.id} value={v.id}>{v.title}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Due Date</label><input className="form-input" type="date" value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
            <div className="form-group"><label className="form-label">Instructions</label><textarea className="form-textarea" placeholder="Describe what employees need to do..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
